/**
 * Heart-icon dropdown in the navbar — mirrors NotificationsDropdown.
 * Surfaces the user's favorites (professionals, resources, jobs, courses,
 * instructors, events) without leaving the page they're on. Click any row
 * → navigate to that detail page. "View all" → /favorites for the full
 * page with type tabs.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { Heart, ExternalLink } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { favoriteUseCase } from "@/lib/services";
import type {
  FavoriteTargetType,
  GetFavoriteData,
} from "@/lib/services/repository/interfaces/favorite.repository.interface";

const TYPE_LABEL: Record<FavoriteTargetType, string> = {
  resource: "Resource",
  professional: "Professional",
  job: "Job",
  course: "Course",
  instructor: "Instructor",
  event: "Event",
};

function detailHref(fav: GetFavoriteData): string {
  switch (fav.targetType) {
    case "resource":
      return `/curriculum/${fav.targetId}`;
    case "course":
      return `/courses/${fav.targetId}`;
    case "instructor":
    case "professional":
      return `/instructors/${fav.targetId}`;
    case "job":
      return `/jobs/${fav.targetId}/view`;
    case "event":
      return `/events/${fav.targetId}`;
    default:
      return "/";
  }
}

export function FavoritesDropdown() {
  const { user } = useFirebaseAuth();
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const userId = user?.id;
  const { data: favorites = [], isLoading } = useQuery({
    queryKey: ["favorites", userId],
    queryFn: () => favoriteUseCase.getUserFavorites(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });

  // Show the most-recent 8 in the dropdown, full list lives at /favorites.
  const recent = useMemo(() => {
    const sorted = [...favorites].sort((a, b) => {
      const at = a.createdAt && typeof a.createdAt === "object" && "seconds" in a.createdAt
        ? (a.createdAt as { seconds: number }).seconds
        : 0;
      const bt = b.createdAt && typeof b.createdAt === "object" && "seconds" in b.createdAt
        ? (b.createdAt as { seconds: number }).seconds
        : 0;
      return bt - at;
    });
    return sorted.slice(0, 8);
  }, [favorites]);

  if (!user) return null;

  const total = favorites.length;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Favorites (${total})`}
          className="relative text-gray-300 hover:text-white"
        >
          <Heart className={`h-5 w-5 ${total > 0 ? "fill-rose-500 text-rose-500" : ""}`} />
          {total > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center">
              {total > 99 ? "99+" : total}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[480px] w-[calc(100vw-1rem)] max-w-80 overflow-hidden border-slate-700 bg-slate-900 text-white"
      >
        <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
          <span className="text-sm font-semibold">Favorites</span>
          <Link href="/favorites">
            <button
              className="text-xs text-cyan-400 hover:text-cyan-300"
              onClick={() => setIsOpen(false)}
            >
              View all
            </button>
          </Link>
        </div>

        {isLoading ? (
          <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">
            <Heart className="h-6 w-6 mx-auto mb-2 text-slate-600" />
            <p>No favorites yet</p>
            <p className="text-xs mt-1 text-slate-500">
              Tap the ❤ on any resource, professional, job, or course to save it here.
            </p>
          </div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto py-1">
            {recent.map((fav) => {
              const unavailable = fav.targetData?.unavailable === true;
              return (
                <button
                  key={`${fav.targetType}::${fav.targetId}`}
                  onClick={() => {
                    // Deleted targets have no destination — don't navigate to a
                    // broken page.
                    if (unavailable) return;
                    setIsOpen(false);
                    setLocation(detailHref(fav));
                  }}
                  className={`w-full text-left px-3 py-2 flex items-start gap-2 group ${
                    unavailable ? 'cursor-default opacity-60' : 'hover:bg-slate-800'
                  }`}
                >
                  <Heart
                    className={`h-4 w-4 mt-0.5 shrink-0 ${
                      unavailable ? 'text-slate-500' : 'text-rose-400 fill-rose-400'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">
                      {TYPE_LABEL[fav.targetType]}
                    </div>
                    <div className="text-sm font-medium truncate">
                      {unavailable ? (
                        <span className="italic text-slate-500">No longer available</span>
                      ) : (
                        fav.targetData?.title || fav.targetId
                      )}
                    </div>
                  </div>
                  {!unavailable && (
                    <ExternalLink className="h-3.5 w-3.5 text-slate-500 group-hover:text-slate-300 mt-1 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default FavoritesDropdown;
